import Model from './Model';
import Lights from './Lights';
import WebViewerOptions from './WebViewerOptions';
import { Scene, PerspectiveCamera, WebGLRenderer, Vector3, Object3D } from 'three';
import OrbitControls from 'orbit-controls-es6';

/**
 * The main Revit Web Viewer class
 * @class RevitWebViewer
 */
class WebViewer {
    /**
     * Creates an instance of RevitWebViewer.
     * @memberof RevitWebViewer
     */
    constructor(options) {
        /**
         * Render the scene via three.js renderer
         * @memberof WebViewer
         */

        this.options = options || new WebViewerOptions(false);

        this.render = () => {
            this.orbitControls.update();
            requestAnimationFrame(this.render);

            if (this.resizeRendererToDisplaySize(this.renderer)) {
                this.resize();
            }

            this.renderer.render(this.scene, this.orbitControls.object);
            if (this.model && this.model.pivot && this.model.pivot.rotation && 0.0 !== this.options.rotation) {
                this.model.pivot.rotation.y -= this.options.rotation;
            }
        };
        this.scene = new Scene();
        this.camera = new PerspectiveCamera(
            25, // fov — Camera frustum vertical field of view.
            window.innerWidth / window.innerHeight, // spect — Camera frustum aspect ratio.
            10, // near — Camera frustum near plane.
            1000000 // far — Camera frustum far plane.
        );
        this.renderer = new WebGLRenderer(// the WebGL renderer displays your beautifully crafted scenes using WebGL.
            {
                alpha: true,
                antialias: true,
                preserveDrawingBuffer: true
            });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.model = null;
        this.lights = null;
    }

    resizeRendererToDisplaySize(renderer) {
        const canvas = renderer.domElement;
        const width = canvas.clientWidth;
        const height = canvas.clientHeight;
        const needResize = canvas.width !== width || canvas.height !== height;
        if (needResize) {
          renderer.setSize(width, height, false);
        }
        return needResize;
      }

    init(element, callback) {
        this.wrapper = element;
        // tweaking the renderer object
        this.renderer.setClearColor(0x000000, 0.0);

        this.renderer.shadowMap.enabled = false;
        // tweaking camera object
        this.camera.position.set(-window.innerHeight, window.innerHeight / 6, -window.innerHeight);
        // set up orbit controls object
        this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
        this.orbitControls.enableZoom = true;
        this.orbitControls.enableKeys = false;
        this.orbitControls.zoomSpeed = 1.0;
        this.orbitControls.enablePan = true; // Set to false to disable panning (ie vertical and horizontal translations)
        // tweak orbit controls in order to uncover the "top edge" of the object
        this.orbitControls.target.set(0, -window.innerHeight / 2, 0);
        this.renderer.setSize(this.wrapper.offsetWidth, this.wrapper.offsetHeight);
        this.wrapper.appendChild(this.renderer.domElement);
        // Set background color of the body element if needed
        if (this.options.background) {
            document.getElementsByTagName("body")[0].style.background = this.options.background;
        }
        this.bindEvents();
        this.render();

        if (callback) {
            console.log('Calling callback function');
            callback();
            this.resize();
        }
    }

    /**
     * Tweak orbit controls and camepra position
     * @memberof WebViewer
     */
  tweakCameraAndControls() {
      if (this.model === null)
        return;

        // Re-compute bounds after moving the object
        this.model.getBoundingFigures();

        // point the camera at the center of the sphere
        this.orbitControls.target = this.model.boundingSphere.center;
        let zAxis = new Vector3(0, 0, 1);
        let direction = zAxis.applyQuaternion(this.orbitControls.object.quaternion);
        let radius = this.model.boundingSphere.radius;
        let offset = radius / Math.tan(Math.PI / 180.0 * this.orbitControls.object.fov * 0.5);
        direction.multiplyScalar(offset);
        let newCameraPosition = new Vector3();
        newCameraPosition.addVectors(this.orbitControls.target, direction);
        this.camera.position.set(newCameraPosition.x, newCameraPosition.y, newCameraPosition.z);
        this.orbitControls.object.updateProjectionMatrix();
        this.camera.updateProjectionMatrix();
    }

    /**
     * Bind global document / window events
     * @memberof WebViewer
     */
    bindEvents() {
        // window resize event
        window.addEventListener('resize', () => {
            if (this.resizeRendererToDisplaySize(this.renderer)) {
                this.resize();
            }

        }, false);
    }

    resize() {
        const canvas = this.renderer.domElement;
        this.camera.aspect = canvas.clientWidth / canvas.clientHeight;
        this.camera.updateProjectionMatrix();
        this.orbitControls.object.aspect = canvas.clientWidth / canvas.clientHeight;
        this.orbitControls.object.updateProjectionMatrix();
    }

    unCenterEmptyBoxes() {
      if (this.model === null)
        return;

      this.model.object.traverse((child) => {
        if (!(child.type === 'Mesh' && 'BoxGeometry' === child.geometry.type))
          return false;

        var width = child.geometry.parameters.width;
        var height = child.geometry.parameters.height;
        var depth = child.geometry.parameters.depth;
        child.geometry.translate(-(width / 2), height / 2, -(depth / 2));
        return true;
      });
    }

  loadScene(scene, callback) {

    if (this.scene === null)
      return;

    try {
      let object3D = Model.load(scene);
      if (object3D === null) {
        this.scene = new Scene();
        this.model = new Model(new Object3D());
        console.err("Object3D is null");
        return;
      }

      let modelIsEmpty = this.model === null || typeof this.model.object === 'undefined';
      if (modelIsEmpty) {
        this.scene = new Scene();
        this.model = new Model(object3D);
        this.lights = new Lights(this.model.boundingSphere);
        this.lights.applyLights(this.scene, this.model.pivot);
        this.scene.add(this.model.pivot); // add( this.model.object );
      } else {
        this.scene.remove(this.model.pivot);
        this.model = new Model(object3D);
        this.scene.add(this.model.pivot); // add( this.model.object );
      }

      this.unCenterEmptyBoxes(); // HACK: from Three.js docs "On creation, the cuboid is centred on the origin..."
      this.tweakCameraAndControls();

      if (this.sceneReady instanceof Function)
        this.sceneReady(this);

      if (callback) {
        callback();
      }

      this.resize();
    } catch(err) {
      // TODO handle the error
      console.log(err);
    }
  }
}

export { WebViewer as default };
